import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
	ICategoryShort,
	ISubCategoryShort,
	ITaskInfo,
	ITaskStep,
	ICategoryStep,
	IAsmxDto,
	IComment,
	INewsResponse,
	ITaskTemplate,
} from '@valhalla/data/entities';
import { from, Observable, of, throwError, zip } from 'rxjs';
import { map, mergeMap, catchError, tap } from 'rxjs/operators';
import ApiVersion from '../api-versions';
import { DataHttpCommonService } from '../data-http-common.service';
import { EndpointUrlConfig } from '../endpoint.config';
import type { IEndpointUrlConfig } from '../endpoint.config';
import {
	ICategoryInfoCriteria,
	ICategoryTreeCriteria,
	ICategoryTreeResult,
	ISubCategoryInfoCriteria,
	ITaskFeedsCriteria,
	ITaskFeedsResult,
	ITaskGridBlockDataCriteria,
	ITaskGridBlockDataResult,
	ITaskInfoCriteria,
	ITaskMainRouteResult,
	ITaskRouteCriteria,
	ITaskStepsCriteria,
	ICategoryStepsCriteria,
	TaskDataHttpService,
	IExecuteTaskStepCriteria,
	IExecuteTaskStepResult,
	ITaskExistsCriteria,
	ITaskExistsResult,
	ITaskPinCriteria,
	ITaskPinResult,
	ITaskUnPinCriteria,
	ITaskRemoveFromFavoritesCriteria,
	ITaskAddToFavoritesCriteria,
	IExecuteTaskActionCriteria,
	IExecuteTaskActionResult,
	IExecuteSignatureResolutionCriteria,
	IExecuteSignatureResolutionResult,
	ITaskSubscribeUserCriteria,
	ITaskNewsCriteria,
	ITaskRecurrenceCriteria,
	ITaskRecurrence,
	ITaskDisableRecurrenceCriteria,
	IGanttCategoryCriteria,
	IGanttSubcategoryCriteria,
	ITaskSearchCriteria,
	TaskFeedType,
	TaskFeedsFilter,
	TaskFeedsFilterOperation,
	TaskFeedsSorter,
	SortType,
	TaskFeedInclude,
	IStatusesResponse,
	IChangeTaskStatusCriteria,
	ITaskChangeOwnerCriteria,
	ITaskChangePriorityCriteria,
	ITaskChangeDueDateCriteria,
	ITaskChangeSignatureReasonCriteria,
	ITaskUpdateStartTimeCriteria,
	ITaskFilesCriteria,
	ITaskPinFileCriteria,
	ITaskUnpinFileCriteria,
	ITaskDeleteFileCriteria,
	INtfTaskTemplateCriteria,
	INtfTaskTemplate,
	ICreateTask,
	ICreateNewDocCriteria,
	ITaskChangeDueDate2Criteria,
	ITaskSetConfidential,
	IGetTaskReportsResult,
	ICheckExistAndAccess,
	IExecuteTaskStepWithCommentCriteria,
	IExecuteTaskStepWithCommentResult,
	ITaskEmailsCriteria,
	ITaskEmailsResponse,
	ITaskClearDeletedFilesCriteria,
	IUpdateTaskAvatarCriteria,
	ISignaturesRes,
	IFileExistanceDto,
	ITaskUpdateFavoritesCriteria,
} from './abstract';
import { IApiResponse } from '../api-response';
import { excludeUndefinedProps } from '@valhalla/utils';
import { FeedTasksType } from '../feeds';
import { ISubcatInfo } from '../../entities/subcat';
import { ICreateTaskResponse, IRequestedSignature, ITaskFullSnapshot } from '.';

@Injectable()
export class TaskDataHttpServiceImpl implements TaskDataHttpService {
	constructor(
		@Inject(EndpointUrlConfig) public readonly config: IEndpointUrlConfig,
		public readonly http: HttpClient,
		public readonly common: DataHttpCommonService
	) {}

	feeds(criteria: ITaskFeedsCriteria): Observable<ITaskFeedsResult> {
		const url = this.common.getApiUrl(this.config.task.feeds, ApiVersion.v10);

		return this.http.post<ITaskFeedsResult>(url, criteria);
	}

	search(criteria: ITaskSearchCriteria): Observable<ITaskFeedsResult> {
		const url = this.common.getApiUrl('task/feeds', ApiVersion.v10);
		const body: ITaskFeedsCriteria = {
			limit: criteria.take,
			offset: criteria.skip,
			filters: [
				{
					filterType: TaskFeedsFilter.search,
					filterOperation: TaskFeedsFilterOperation.searchOperation,
					filterValues: Array.isArray(criteria.filter) ? criteria.filter : [criteria.filter],
				},
			],
			feedTypes: criteria.feedTypes ? criteria.feedTypes : [FeedTasksType.all],
			extendedSearch: true,
			sorters: [{ sorterType: TaskFeedsSorter.lastCommentDate, sortDir: SortType.desc }],
			include: [TaskFeedInclude.subscribers],
			showClosed: false,
			showLastComment: true,
			subcatId: null,
			catId: null,
			summaryId: null,
		};
		return this.http.post<ITaskFeedsResult>(url, body);
	}

	getInfo(criteria: ITaskInfoCriteria): Observable<ITaskInfo> {
		const url = this.common.getApiUrl(`mobile/tasks/${criteria.taskId}`, ApiVersion.v10);
		return this.http.get<ITaskInfo>(url).pipe(tap(ti => this.adjustTaskInfo(ti)));
	}

	getInfoParts({ taskId, parts }: ITaskInfoCriteria): Observable<ITaskInfo> {
		const url = this.common.getApiUrl(`tasks`);
		return this.http
			.post<ITaskInfo>(
				url,
				{ taskId, selectedInfoParts: parts },
				{
					headers: {
						'Background-request': 'true',
					},
				}
			)
			.pipe(tap(ti => this.adjustTaskInfo(ti)));
	}

	getNews({ taskId }: ITaskNewsCriteria): Observable<INewsResponse> {
		const url = this.common.getApiUrl(`feeds/task/${taskId}?isCropHtml=false`, ApiVersion.v10);
		return this.http.get<INewsResponse>(url);
	}

	getGanttCategoryData({ categoryId }: IGanttCategoryCriteria): Observable<any> {
		const url = this.common.getEndpointUrl(`/Gantt.aspx/GetData?CategoryId=${categoryId}`);
		return this.http
			.post<IAsmxDto>(url, {
				parameters: { DateStart: null, DateEnd: null, All: null, CategoryId: categoryId },
				type: 5,
			})
			.pipe(map(res => res.d));
	}

	getGanttSubcategoryData({ subcategoryId }: IGanttSubcategoryCriteria): Observable<any> {
		const url = this.common.getEndpointUrl(`/Gantt.aspx/GetData?CategoryId=${subcategoryId}`);
		return this.http
			.post<IAsmxDto>(url, {
				parameters: { DateStart: null, DateEnd: null, All: null, SubcatId: subcategoryId },
				type: 4,
			})
			.pipe(map(res => res.d));
	}

	setLike({ taskId }: ITaskNewsCriteria): Observable<any> {
		const url = this.common.getApiUrl(`tasks/${taskId}/like`, ApiVersion.v10);
		return this.http.post<any>(url, { taskId });
	}

	getTasks({ subcatId, limit, offset, blockId, showClosed, showRejected, isCropHtml, search }) {
		const url = this.common.getApiUrl(`feeds/tasks/${subcatId}`, ApiVersion.v10);

		return this.http.get<any>(url, {
			params: excludeUndefinedProps({
				limit,
				offset,
				blockId,
				showClosed,
				showRejected,
				isCropHtml,
				search: search ? search : undefined,
			}),
		});
	}

	getTaskRecurrence({ taskId }: ITaskRecurrenceCriteria): Observable<ITaskRecurrence> {
		const url = this.common.getApiUrl(`tasks/${taskId}/recurrence`, ApiVersion.v12);
		return this.http.get<ITaskRecurrence>(url);
	}

	disableTaskRecurrence({ taskId }: ITaskDisableRecurrenceCriteria): Observable<any> {
		const url = this.common.getApiUrl(`tasks/${taskId}/recurrence/delete`, ApiVersion.v12);
		return this.http.post<any>(url, { taskId });
	}

	saveTaskRecurrence(data: ITaskRecurrence): Observable<any> {
		const url = this.common.getApiUrl(`tasks/recurrence`, ApiVersion.v12);
		return this.http.post<any>(url, data);
	}

	findNewsTemplate(taskTemplates: ITaskTemplate[]): ITaskTemplate {
		if (!taskTemplates?.length) {
			return;
		}

		const isNewsTemplate = (template: ITaskTemplate) =>
			template && template?.typeId === 'static' && template?.name?.toLowerCase().trim() === 'новости';

		return taskTemplates.find(t => isNewsTemplate(t));
	}

	removeTask(criteria: ITaskInfoCriteria): Observable<any> {
		return this.http.post<any>('/TCWeb.asmx/DeleteTask', criteria);
	}

	addToFavorites({ taskId, color, favsFolderId }: ITaskAddToFavoritesCriteria): Observable<any> {
		const url = this.common.getApiUrl('/tasks/set-color');
		return this.http.post<any>(url, {
			taskId: taskId,
			color: color?.toUpperCase() || '#FFFFFE',
			favsFolderId: favsFolderId || 1,
		});
	}

	removeFromFavorites({ taskId }: ITaskRemoveFromFavoritesCriteria): Observable<any> {
		const url = this.common.getApiUrl('/tasks/set-color');
		return this.http.post(url, { taskId: taskId, color: '#FFFFFF', favsFolderId: null });
	}

	updateFavorites({ taskId, color, favsFolderIds }: ITaskUpdateFavoritesCriteria): Observable<any> {
		const url = this.common.getApiUrl('/tasks/set-color-favorites');
		return this.http.post(url, {
			taskId: taskId,
			color: color?.toUpperCase() || '#FFFFFE',
			favsFolderIds: favsFolderIds,
		});
	}

	//used in the old version of mtf
	unsubscribeFromTask(criteria: ITaskInfoCriteria): Observable<any> {
		return this.http.post<any>('/TCWeb.asmx/UnsubscribeFromTask', criteria);
	}

	unsubscribeFromTask2(taskId: number): Observable<any> {
		const url = this.common.getApiUrl(`tasks/unsubscribe/${taskId}`);
		return this.http.post(url, null);
	}

	getRoute(criteria: ITaskRouteCriteria): Observable<ITaskMainRouteResult> {
		const url = this.common.getApiUrl(`tasks/${criteria.taskId}/mainroute`, ApiVersion.v10);
		return this.http.get<ITaskMainRouteResult>(url);
	}

	getSteps(criteria: ITaskStepsCriteria): Observable<ITaskStep[]> {
		const url = this.common.getApiUrl(`tasks/${criteria.taskId}/steps`, ApiVersion.v10);
		return this.http.get<ITaskStep[]>(url);
	}

	getCategorySteps(criteria: ICategoryStepsCriteria): Observable<ICategoryStep> {
		const url = this.common.getApiUrl(`subcategories/${criteria.subcatId}/steps`, ApiVersion.v10);
		return this.http.get<ICategoryStep>(url);
	}

	getGridBlockData(criteria: ITaskGridBlockDataCriteria): Observable<ITaskGridBlockDataResult> {
		const url = this.common.getApiUrl(`tasks/feeds/usedinblock/${criteria.blockId}/${criteria.taskId}`, ApiVersion.v10);
		return this.http.get<ITaskGridBlockDataResult>(url).pipe(
			map(result => {
				// if no content - return safe object
				result = result || {
					categories: [],
					tabs: [],
					tasks: [],
					templates: [],
				};
				return result;
			})
		);
	}

	executeStep(criteria: IExecuteTaskStepCriteria): Observable<IExecuteTaskStepResult> {
		const url = this.common.getApiUrl(`task/step/${criteria.taskId}`, ApiVersion.v10);
		return this.http.post<IExecuteTaskStepResult>(url, {
			stepId: criteria.stepId,
			comment: criteria.comment || null,
		});
	}

	executeStepWithComment(
		taskId: number,
		criteria: IExecuteTaskStepWithCommentCriteria
	): Observable<IExecuteTaskStepWithCommentResult> {
		const url = this.common.getApiUrl(`tasks/${taskId}/change-step`);
		return this.http.post<IExecuteTaskStepWithCommentResult>(url, criteria);
	}

	executeAction({
		actionId,
		taskId,
		comment,
		signatures,
	}: IExecuteTaskActionCriteria): Observable<IExecuteTaskActionResult> {
		const url = this.common.getApiUrl(`mobile/tasks/${taskId}/actions/${actionId}`, ApiVersion.v11);
		return this.http
			.post<IExecuteTaskActionResult>(url, {
				comment: comment,
				signatures: signatures,
			})
			.pipe(
				map(response => this.common.normalizeResponseDto(response)),
				tap(res => {
					if (typeof res.redirectResult === 'object') {
						res.redirectResult = this.common.normalizeResponseDto(res.redirectResult);
					}
				}),
				catchError(err => {
					const error = err.error;
					if (error && error.Message) {
						const data: IExecuteTaskActionResult = {
							success: error.Success,
							message: error.Message,
							redirectResult: error.RedirectResult,
						};
						return of(data);
					}
					return throwError(() => err);
				})
			);
	}

	executeSignatureResolution(
		criteria: IExecuteSignatureResolutionCriteria
	): Observable<IExecuteSignatureResolutionResult> {
		const { signatureId, signatureAction, params } = criteria;

		const url = this.common.getApiUrl(`tasks/signatures/${signatureId}/${signatureAction}`);

		return this.http.post<IApiResponse<IExecuteSignatureResolutionResult>>(url, params || {}).pipe(map(r => r.data));
	}

	getCategoryShortInfo(criteria: ICategoryInfoCriteria): Observable<ICategoryShort> {
		const url = this.common.getApiUrl(`categories/${criteria.categoryId}/short`, ApiVersion.v10);
		return this.http.get<ICategoryShort>(url);
	}
	getSubCategoryShortInfo(criteria: ISubCategoryInfoCriteria): Observable<ISubCategoryShort> {
		const url = this.common.getApiUrl(`subcategories/${criteria.subCategoryId}/short`, ApiVersion.v10);
		return this.http.get<ISubCategoryShort>(url);
	}

	getSubCategoryInfo(criteria: ISubCategoryInfoCriteria): Observable<ISubcatInfo> {
		const url = this.common.getApiUrl(`storage/subcategories/${criteria.subCategoryId}`);
		return this.http.get<ISubcatInfo>(url);
	}

	getSubcategoryTemplate(criteria: INtfTaskTemplateCriteria): Observable<INtfTaskTemplate> {
		const url = this.common.getApiUrl(`tasks/template`, ApiVersion.v10);
		const body = criteria;
		return this.http.post<INtfTaskTemplate>(url, body);
	}

	getCategoryTree(criteria: ICategoryTreeCriteria): Observable<ICategoryTreeResult> {
		return this.getCategoryTreeRecordsRecursive(criteria, {}).pipe(
			map(records => ({
				records: records,
			}))
		);
	}

	isExists({ taskId }: ITaskExistsCriteria): Observable<ITaskExistsResult> {
		const url = this.common.getEndpointUrl('/TCWeb.asmx/CheckTaskForExistAccess');
		return this.http.post<IAsmxDto<boolean>>(url, { taskId }).pipe(
			map(response => {
				const result: ITaskExistsResult = {
					isExists: response.d,
				};
				return result;
			})
		);
	}

	subscribeUsers({ taskId, userIds }: ITaskSubscribeUserCriteria): Observable<any> {
		const urls = userIds.map(id => this.common.getApiUrl(`task/${taskId}/subscribe/user/${id}`, ApiVersion.v12));
		const requests$ = urls.map(url => this.http.post(url, null));

		return zip(...requests$);
	}

	unsubscribeUsers({ taskId, userIds }: ITaskSubscribeUserCriteria): Observable<any> {
		const urls = userIds.map(id => this.common.getApiUrl(`task/${taskId}/unsubscribe/user/${id}`, ApiVersion.v12));
		const requests$ = urls.map(url => this.http.post(url, null));

		return zip(...requests$);
	}

	protected adjustTaskInfo(ti: ITaskInfo): ITaskInfo {
		if (Array.isArray(ti.comments)) {
			ti.comments.forEach(this.adjustComment);
		}

		return ti;
	}

	protected adjustComment(comment: Partial<IComment>): Partial<IComment> {
		comment.content = comment.text;
		comment.userId = comment.userId || (comment.sender && comment.sender.userId);
		comment.userName = comment.userName || (comment.sender && comment.sender.userName);
		comment.commentId = comment.id;
		comment.replyComment = comment.replyComment || comment.inReplyToComment;

		return comment;
	}

	protected getCategoryTreeRecordsRecursive(
		criteria: ICategoryTreeCriteria,
		records: Record<number, any>
	): Observable<Record<number, any>> {
		// later add from task id
		return of(criteria).pipe(
			mergeMap(({ subCategoryId, categoryId }) => {
				if (subCategoryId) {
					return this.getSubCategoryShortInfo({ subCategoryId: subCategoryId });
				}
				if (categoryId) {
					return this.getCategoryShortInfo({ categoryId: categoryId });
				}
				return throwError('invalid params for getCategoryTreeRecordsRecursive, subCategoryId, categoryId are empty');
			}),
			mergeMap(data => {
				records[data.id] = data;
				if (data.parentCategoryId) {
					return this.getCategoryTreeRecordsRecursive({ categoryId: data.parentCategoryId }, records);
				}
				return of(records);
			})
		);
	}

	getStatuses(taskId: number): Observable<IStatusesResponse> {
		const url = this.common.getApiUrl(`tasks/${taskId}/states`);
		return this.http.get<IApiResponse<IStatusesResponse>>(url).pipe(map(res => res.data));
	}

	changeTaskStatus({ taskId, newStateId }: IChangeTaskStatusCriteria): Observable<any> {
		const url = this.common.getApiUrl(`tasks/${taskId}/changeState`);
		return this.http.post(url, { newStateId });
	}

	pin({ taskId }: ITaskPinCriteria): Observable<ITaskPinResult> {
		const url = this.common.getApiUrl(`mobile/tasks/${taskId}/pin`, ApiVersion.v12);
		return this.http.post(url, undefined);
	}

	unpin({ taskId }: ITaskUnPinCriteria): Observable<ITaskPinResult> {
		const url = this.common.getApiUrl(`mobile/tasks/${taskId}/unpin`, ApiVersion.v12);
		return this.http.post(url, undefined);
	}

	lockDueDate(taskId: number): Observable<any> {
		const url = this.common.getApiUrl(`tasks/${taskId}/lock-due-date`);
		return this.http.post(url, undefined);
	}

	unlockDueDate(taskId: number): Observable<any> {
		const url = this.common.getApiUrl(`tasks/${taskId}/unlock-due-date`);
		return this.http.post(url, undefined);
	}

	assignExecutor(taskId: number, body: { add: number[]; remove: number[] }): Observable<void> {
		return this.http.post<void>(this.common.getApiUrl(`tasks/${taskId}/change-performers`), body);
	}

	assignResponsible(taskId: number, performerId: number): Observable<void> {
		return this.http.post<void>(
			this.common.getApiUrl(`tasks/${taskId}/performers/${performerId}/isResponsible`, ApiVersion.v10),
			null
		);
	}

	changeOwner({ taskId, newOwnerId }: ITaskChangeOwnerCriteria): Observable<any> {
		const url = this.common.getApiUrl(`tasks/change-owner`);
		return this.http.post(url, { taskId, newOwnerId });
	}

	changePriority({ taskId, priority }: ITaskChangePriorityCriteria): Observable<any> {
		const url = this.common.getApiUrl(`tasks/${taskId}/set-priority`);
		return this.http.post(url, { priority });
	}

	changeDueDate(criteria: ITaskChangeDueDateCriteria): Observable<any> {
		const url = this.common.getApiUrl(`tasks/duedate`, ApiVersion.v12);
		return this.http.post(url, criteria);
	}

	changeDueDate2({ taskId, newDate, reason, аddToQuickReply }: ITaskChangeDueDate2Criteria): Observable<any> {
		const url = this.common.getApiUrl(`tasks/${taskId}/change-duedate`);
		return this.http.post(url, {
			newDate,
			reason,
			аddToQuickReply,
		});
	}

	changeSignatureReason(criteria: ITaskChangeSignatureReasonCriteria): Observable<any> {
		const url = '/TCWeb.asmx/ChangeSignatureReason';
		return this.http.post(url, criteria);
	}

	updateTaskStartTime(criteria: ITaskUpdateStartTimeCriteria): Observable<any> {
		const url = '/TCWeb.asmx/UpdateTaskStartTime';
		return this.http.post(url, criteria);
	}

	getFiles(criteria: ITaskFilesCriteria): Observable<any> {
		const url = this.common.getApiUrl(`task/files`);
		return this.http.post<IApiResponse<any>>(url, criteria).pipe(map(res => res.data));
	}

	clearDeletedFiles(criteria: ITaskClearDeletedFilesCriteria): Observable<any> {
		const url = this.common.getApiUrl(`task/files/clear-deleted-files/${criteria.taskId}`);
		return this.http.post<IApiResponse<any>>(url, criteria).pipe(map(res => res));
	}

	pinFile(criteria: ITaskPinFileCriteria): Observable<any> {
		const url = this.common.getApiUrl(`task/files/pin/${criteria.taskId}/${criteria.fileId}`);
		return this.http.post<IApiResponse<any>>(url, criteria);
	}

	unpinFile(criteria: ITaskUnpinFileCriteria): Observable<any> {
		const url = this.common.getApiUrl(`task/files/unpin/${criteria.taskId}/${criteria.fileId}`);
		return this.http.post<IApiResponse<any>>(url, criteria);
	}

	deleteFile(criteria: ITaskDeleteFileCriteria): Observable<any> {
		const url = this.common.getApiUrl(
			`filestorage/${criteria.parentFolderType}/${criteria.parentFolderId}/file/${criteria.fileId}/delete`,
			ApiVersion.v12
		);
		return this.http.post(url, criteria);
	}

	deleteTask(criteria: number[]): Observable<any> {
		const url = this.common.getApiUrl(`tasks/delete`);
		return this.http.post<IApiResponse<any>>(url, criteria);
	}

	getUnreadNews(): Observable<any> {
		const url = this.common.getApiUrl(`leadin/contentId`, ApiVersion.v10);
		return this.http.post<IApiResponse<number>>(url, null);
	}

	getUnreadNewsContent(criteria: number): Observable<any> {
		const url = this.common.getApiUrl(`leadin/content/${criteria}`, ApiVersion.v10);
		return this.http.post<IApiResponse<any>>(url, null);
	}

	closeUnreadNewsContent(criteria: number): Observable<any> {
		const url = this.common.getApiUrl(`leadin/close/${criteria}`, ApiVersion.v10);
		return this.http.post<IApiResponse<any>>(url, null);
	}

	createTask(criteria: ICreateTask): Observable<ICreateTaskResponse> {
		const url = this.common.getApiUrl(`tasks?v=1.1`, ApiVersion.v11);
		return this.http.post<ICreateTaskResponse>(url, criteria);
	}

	createNewDoc(criteria: ICreateNewDocCriteria): Observable<any> {
		const url = this.common.getApiUrl(`tasks/${criteria.taskId}/new-doc/${criteria.docType}`);
		return this.http.post<IApiResponse<any>>(url, null);
	}

	encryptTask(taskId: number) {
		const url = this.common.getApiUrl(`tasks/${taskId}/encrypt`);
		return this.http.post<IApiResponse<any>>(url, null);
	}

	setConfidentialTask({ taskId, isConfidential }: ITaskSetConfidential) {
		const url = this.common.getApiUrl(`tasks/${taskId}/set-confidential/${isConfidential}`);
		return this.http.post<IApiResponse<any>>(url, null);
	}

	getReports(taskId: number): Observable<IGetTaskReportsResult> {
		const url = this.common.getApiUrl(`mobile/containers/task/ReportsList/${taskId}`, ApiVersion.v12);
		return this.http.post<any>(url, null).pipe(
			map(response => {
				const template = response?.data?.find(t => t.id === 'ReportsList');
				const reportsRecords = template?.blocks?.find(b => b.action === 'ReportsList')?.data?.reports || {};
				const reports = Object.entries(reportsRecords).map(([id, name]) => ({ id: Number(id), name: String(name) }));
				const result: IGetTaskReportsResult = {
					reports,
				};
				return result;
			})
		);
	}

	checkExistAndAccess(taskId: number): Observable<ICheckExistAndAccess> {
		const url = this.common.getApiUrl(`tasks/check-exist-and-access/${taskId}`);
		return this.http.get<IApiResponse<ICheckExistAndAccess>>(url).pipe(map(res => res.data));
	}

	getEmails(criteria: ITaskEmailsCriteria): Observable<ITaskEmailsResponse[]> {
		const url = this.common.getApiUrl(`task/emails`);
		return this.http.post<IApiResponse<ITaskEmailsResponse[]>>(url, criteria).pipe(map(res => res.data));
	}

	updateTaskAvatar({ taskId, fileName, base64File }: IUpdateTaskAvatarCriteria): Observable<any> {
		const url = this.common.getApiUrl(`task/avatar/${taskId}`);
		return this.http.post<any>(url, { fileName, base64File });
	}

	removeTaskAvatar({ taskId }: Pick<IUpdateTaskAvatarCriteria, 'taskId'>): Observable<any> {
		const url = this.common.getApiUrl(`task/avatar/${taskId}/delete`);
		return this.http.post<any>(url, undefined);
	}

	getTaskAvatar(taskId: number): Observable<null | string> {
		const url = this.common.getEndpointUrl(`/GetTaskAvatar.ashx?id=${taskId}`);
		return from(fetch(url).then(r => r.text()));
	}

	getTaskAvatarUrl(taskId: number): string {
		const url = this.common.getEndpointUrl(`/GetTaskAvatar.ashx?id=${taskId}`);
		return url;
	}

	getSignatureHistory(taskId: number, request: any): Observable<IRequestedSignature[]> {
		const url = this.common.getApiUrl(
			`tasks/Signature/history/${taskId}?hideDeletedSignatures=${request.hideClosed}&showFinalOnly=${request.showFinalOnly}`,
			ApiVersion.v10
		);
		return this.http.get<IApiResponse<IRequestedSignature[]>>(url).pipe(map(res => res.data));
	}

	getTaskSignatureSnapshot(taskSignatureId: number): Observable<ITaskFullSnapshot> {
		const url = this.common.getApiUrl(`tasks/snapshot/${taskSignatureId}`);
		return this.http.get<IApiResponse<ITaskFullSnapshot>>(url).pipe(map(res => res.data));
	}

	getSignatures(): Observable<ISignaturesRes> {
		const url = this.common.getEndpointUrl(`/iosclientservices/DataService.asmx/GetSignatureRequests`);
		return this.http.post<any>(url, {}).pipe(
			map(res => {
				return res?.d;
			})
		);
	}

	getExistanceFileByName(criteria: { taskId: number; fileNames: string[] }): Observable<IFileExistanceDto[]> {
		const url = this.common.getApiUrl(`task/files/existance`);
		return this.http.post<IApiResponse<any>>(url, criteria).pipe(map(res => res.data));
	}
}
